import { Fragment, type FunctionalComponent, h } from '@stencil/core'
import type { Account } from '../utils/api'
import { type DebugConfig, type SelectedAccount, storage } from '../utils/storage'

const renderFavoriteIcon = (isFavorite: boolean) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={isFavorite ? 'currentColor' : 'none'}
    stroke="currentColor"
    stroke-width="2"
  >
    <title>Favorite icon</title>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
)

export interface AccountPanelProps {
  accountsData: Account[]
  selectedAccount: SelectedAccount | null
  onSelectAccount: (accountId: string) => void
  onRefreshAccounts: () => void
  isLoadingAccount?: boolean
  isLoadingAccounts?: boolean
  accountsError?: string | null
  favoritesOnly: boolean
  favoritesAccountsData: Account[]
  onToggleFavorite?: (accountId: string) => void
  debugConfig?: DebugConfig | null
}

export const AccountPanel: FunctionalComponent<AccountPanelProps> = ({
  accountsData,
  selectedAccount,
  onSelectAccount,
  onRefreshAccounts,
  isLoadingAccount,
  isLoadingAccounts,
  accountsError,
  favoritesOnly,
  favoritesAccountsData,
  onToggleFavorite,
  debugConfig
}) => {
  // Get favorite account IDs from storage
  const favoriteAccounts = storage.getFavoriteAccounts()

  // Handle toggle favorite with optional callback
  const handleToggleFavorite = (accountId: string) => {
    storage.toggleFavoriteAccount(accountId)
    onToggleFavorite?.(accountId)
  }

  const isFavorite = favoriteAccounts.includes(selectedAccount?.id || '')

  return (
    <Fragment>
      <div
        class="debug-toolbar-info"
        style={{ marginBottom: '12px', marginTop: '0' }}
      >
        {debugConfig ? (
          <Fragment>
            <rtr-env-select
              mode="environment"
              asInlineText
              environmentOptions={debugConfig.environments}
              stagingOptions={debugConfig.stagingOptions}
              baseUrls={debugConfig.baseUrls}
              urlPattern={debugConfig.urlPattern}
            />
            <rtr-env-select
              mode="staging"
              asInlineText
              environmentOptions={debugConfig.environments}
              stagingOptions={debugConfig.stagingOptions}
              baseUrls={debugConfig.baseUrls}
              urlPattern={debugConfig.urlPattern}
            />
          </Fragment>
        ) : (
          <div
            class="debug-toolbar-info"
            style={{ padding: '12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}
          >
            Configuration unavailable. Environment settings cannot be changed.
          </div>
        )}
      </div>
      <div class="debug-toolbar-control">
        <div class="flex items-center justify-between gap-2">
          <label htmlFor="debugAccountSelect">Select Account</label>
          <button
            class="debug-toolbar-button-link"
            type="button"
            onClick={onRefreshAccounts}
            disabled={isLoadingAccounts}
          >
            {isLoadingAccounts ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        <account-combobox
          accounts={favoritesOnly ? favoritesAccountsData : accountsData}
          selectedAccount={selectedAccount}
          onSelectAccount={onSelectAccount}
          isLoading={isLoadingAccount}
          favoriteAccounts={favoriteAccounts}
          onToggleFavorite={handleToggleFavorite}
        />
        {accountsError && (
          <div
            class="debug-toolbar-error"
            style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c00' }}
          >
            {accountsError}
          </div>
        )}
      </div>
      <div class="debug-toolbar-info">
        <div class="flex items-center gap-2">
          <strong>Access Token: </strong>
          <code>{selectedAccount?.widgetAccessToken || 'Not set'}</code>

          <button
            type="button"
            class={`${isFavorite ? 'active' : ''}`}
            onClick={() => handleToggleFavorite(selectedAccount?.id || '')}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {renderFavoriteIcon(isFavorite)}
          </button>
        </div>
      </div>
    </Fragment>
  )
}
